import React, { useEffect, useState } from 'react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Header } from '../components/Header';
import { Modal } from '../components/Modal';
import { useAppStore } from '../store/useAppStore';
import { apiClient } from '../api/client';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from '../utils/format';
import { languages } from '@alveon/shared/constants';
import { TimezoneSelector } from '../components/TimezoneSelector/TimezoneSelector';
import { ExportFlow, ExportConfig } from '../components/ExportFlow';
import { generateExcelFile } from '../utils/excelExport';
import { useTheme } from '../hooks/useTheme';
import { ThemeMode } from '../types';
import i18n from '../i18n/config';
import { ExportHistoryScreen } from './ExportHistoryScreen';
import { DeleteAccountModal } from '../components/DeleteAccountModal/DeleteAccountModal';
import { DeviceManagementScreen } from './DeviceManagementScreen';
import './SettingsScreen.css';

export const SettingsScreen: React.FC = () => {
  const { settings, setSettings, setCurrentPage } = useAppStore();
  const { t, i18n } = useTranslation();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '' });
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showTimezoneModal, setShowTimezoneModal] = useState(false);
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [currencySearchQuery, setCurrencySearchQuery] = useState('');
  const { themeMode, changeThemeMode } = useTheme();
  
  // Export
  const [showExportModal, setShowExportModal] = useState(false);
  const [showExportHistory, setShowExportHistory] = useState(false);
  
  // Password
  const [hasPassword, setHasPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  
  // Delete Account
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  
  // Device Management
  const [showDeviceManagement, setShowDeviceManagement] = useState(false);
  
  // Получаем данные пользователя из Telegram WebApp
  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
  const userId = tgUser?.id || null;
  
  useEffect(() => {
    loadSettings();
    loadPasswordStatus();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await apiClient.getSettings();
      setSettings(data.settings);
      
      // Синхронизируем язык с i18n
      if (data.settings?.language && ['ru', 'en', 'es'].includes(data.settings.language)) {
        i18n.changeLanguage(data.settings.language);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };
  
  // Вычисляем отображаемое имя пользователя
  const userName = settings?.first_name || tgUser?.username || t('settings.user');

  const loadPasswordStatus = async () => {
    try {
      const response = await apiClient.get<{ has_password: boolean }>('/api/auth/password-status');
      setHasPassword(response.has_password);
    } catch (error) {
      console.error('Error loading password status:', error);
    }
  };

  const handleSetPassword = async () => {
    setPasswordError('');
    
    if (!passwordInput || !confirmPasswordInput) {
      setPasswordError(t('settings.passwordFieldsRequired'));
      return;
    }
    
    if (passwordInput !== confirmPasswordInput) {
      setPasswordError(t('settings.passwordsDoNotMatch'));
      return;
    }
    
    if (passwordInput.length < 8) {
      setPasswordError(t('settings.passwordMinLength'));
      return;
    }
    
    const hasLetters = /[a-zA-Z]/.test(passwordInput);
    const hasNumbers = /[0-9]/.test(passwordInput);
    
    if (!hasLetters || !hasNumbers) {
      setPasswordError(t('settings.passwordLettersDigits'));
      return;
    }
    
    try {
      setIsSettingPassword(true);
      await apiClient.post('/api/auth/set-password', { password: passwordInput });
      setHasPassword(true);
      setShowPasswordModal(false);
      setPasswordInput('');
      setConfirmPasswordInput('');
      alert(hasPassword ? t('settings.passwordChangeSuccess') : t('settings.passwordSetSuccess'));
    } catch (error: any) {
      console.error('Error setting password:', error);
      setPasswordError(error.response?.data?.error || t('settings.passwordSetError'));
    } finally {
      setIsSettingPassword(false);
    }
  };


  const handleCurrencyChange = async (currency: string) => {
    try {
      setIsUpdatingSettings(true);
      const response = await apiClient.updateSettings({ currency });
      setSettings(response.settings);
      setShowCurrencyModal(false);
      console.log('✅ Currency updated to:', currency);
    } catch (error) {
      console.error('Error updating currency:', error);
      alert(t('settings.errorAddingCategory'));
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const handleLanguageChange = async (newLanguage: string) => {
    try {
      setIsUpdatingSettings(true);
      const response = await apiClient.updateSettings({ language: newLanguage });
      setSettings(response.settings);
      i18n.changeLanguage(newLanguage);
      setShowLanguageModal(false);
      console.log('✅ Language updated to:', newLanguage);
    } catch (error) {
      console.error('Error updating language:', error);
      alert(t('common.error'));
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const handleTimezoneChange = async (timezone: string, timezoneCity?: string) => {
    try {
      setIsUpdatingSettings(true);
      const response = await apiClient.updateSettings({ 
        timezone, 
        timezone_city: timezoneCity || timezone.split('/')[1] 
      });
      setSettings(response.settings);
      setShowTimezoneModal(false);
      console.log('✅ Timezone updated to:', timezone);
    } catch (error) {
      console.error('Error updating timezone:', error);
      alert(t('common.error'));
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const currencies = [
    { code: 'RUB', name: 'Российский рубль', symbol: '₽' },
    { code: 'USD', name: 'Доллар США', symbol: '$' },
    { code: 'EUR', name: 'Евро', symbol: '€' },
    { code: 'GBP', name: 'Фунт стерлингов', symbol: '£' },
    { code: 'JPY', name: 'Японская иена', symbol: '¥' },
    { code: 'CNY', name: 'Китайский юань', symbol: '¥' },
    { code: 'KZT', name: 'Казахский тенге', symbol: '₸' },
    { code: 'UAH', name: 'Украинская гривна', symbol: '₴' },
    { code: 'BYN', name: 'Белорусский рубль', symbol: 'Br' },
    { code: 'TRY', name: 'Турецкая лира', symbol: '₺' },
    { code: 'INR', name: 'Индийская рупия', symbol: '₹' },
    { code: 'BRL', name: 'Бразильский реал', symbol: 'R$' },
    { code: 'AUD', name: 'Австралийский доллар', symbol: 'A$' },
    { code: 'CAD', name: 'Канадский доллар', symbol: 'C$' },
    { code: 'CHF', name: 'Швейцарский франк', symbol: 'CHF' },
    { code: 'SEK', name: 'Шведская крона', symbol: 'kr' },
    { code: 'NOK', name: 'Норвежская крона', symbol: 'kr' },
    { code: 'DKK', name: 'Датская крона', symbol: 'kr' },
    { code: 'PLN', name: 'Польский злотый', symbol: 'zł' },
    { code: 'CZK', name: 'Чешская крона', symbol: 'Kč' },
    { code: 'HUF', name: 'Венгерский форинт', symbol: 'Ft' },
    { code: 'RON', name: 'Румынский лей', symbol: 'lei' },
    { code: 'BGN', name: 'Болгарский лев', symbol: 'лв' },
    { code: 'HRK', name: 'Хорватская куна', symbol: 'kn' },
    { code: 'RSD', name: 'Сербский динар', symbol: 'дин' },
    { code: 'AED', name: 'Дирхам ОАЭ', symbol: 'د.إ' },
    { code: 'SAR', name: 'Саудовский риял', symbol: '﷼' },
    { code: 'QAR', name: 'Катарский риал', symbol: '﷼' },
    { code: 'KWD', name: 'Кувейтский динар', symbol: 'د.ك' },
    { code: 'ILS', name: 'Израильский шекель', symbol: '₪' },
    { code: 'EGP', name: 'Египетский фунт', symbol: 'E£' },
    { code: 'ZAR', name: 'Южноафриканский рэнд', symbol: 'R' },
    { code: 'NGN', name: 'Нигерийская найра', symbol: '₦' },
    { code: 'KES', name: 'Кенийский шиллинг', symbol: 'KSh' },
    { code: 'GHS', name: 'Ганский седи', symbol: '₵' },
    { code: 'MXN', name: 'Мексиканское песо', symbol: '$' },
    { code: 'ARS', name: 'Аргентинское песо', symbol: '$' },
    { code: 'CLP', name: 'Чилийское песо', symbol: '$' },
    { code: 'COP', name: 'Колумбийское песо', symbol: '$' },
    { code: 'PEN', name: 'Перуанский соль', symbol: 'S/' },
    { code: 'VES', name: 'Венесуэльский боливар', symbol: 'Bs.' },
    { code: 'UYU', name: 'Уругвайское песо', symbol: '$U' },
    { code: 'KRW', name: 'Южнокорейская вона', symbol: '₩' },
    { code: 'SGD', name: 'Сингапурский доллар', symbol: 'S$' },
    { code: 'HKD', name: 'Гонконгский доллар', symbol: 'HK$' },
    { code: 'TWD', name: 'Новый тайваньский доллар', symbol: 'NT$' },
    { code: 'THB', name: 'Таиландский бат', symbol: '฿' },
    { code: 'MYR', name: 'Малайзийский ринггит', symbol: 'RM' },
    { code: 'IDR', name: 'Индонезийская рупия', symbol: 'Rp' },
    { code: 'PHP', name: 'Филиппинское песо', symbol: '₱' },
    { code: 'VND', name: 'Вьетнамский донг', symbol: '₫' },
    { code: 'PKR', name: 'Пакистанская рупия', symbol: '₨' },
    { code: 'BDT', name: 'Бангладешская така', symbol: '৳' },
    { code: 'LKR', name: 'Шри-ланкийская рупия', symbol: 'Rs' },
    { code: 'NPR', name: 'Непальская рупия', symbol: 'Rs' },
    { code: 'MMK', name: 'Мьянманский кьят', symbol: 'K' },
    { code: 'KHR', name: 'Камбоджийский риель', symbol: '៛' },
    { code: 'LAK', name: 'Лаосский кип', symbol: '₭' },
    { code: 'GEL', name: 'Грузинский лари', symbol: '₾' },
    { code: 'AMD', name: 'Армянский драм', symbol: '֏' },
    { code: 'AZN', name: 'Азербайджанский манат', symbol: '₼' },
    { code: 'UZS', name: 'Узбекский сум', symbol: "so'm" },
    { code: 'KGS', name: 'Киргизский сом', symbol: 'с' },
    { code: 'TJS', name: 'Таджикский сомони', symbol: 'ЅМ' },
    { code: 'TMT', name: 'Туркменский манат', symbol: 'm' },
    { code: 'MDL', name: 'Молдавский лей', symbol: 'L' },
    { code: 'NZD', name: 'Новозеландский доллар', symbol: 'NZ$' },
    { code: 'ISK', name: 'Исландская крона', symbol: 'kr' },
  ];

  const getCurrentLanguageName = () => {
    const lang = languages.find(l => l.code === (settings?.language || i18n.language));
    return lang?.name || 'Русский';
  };

  // Фильтрация валют по поисковому запросу
  const filteredCurrencies = currencies.filter(curr => 
    curr.name.toLowerCase().includes(currencySearchQuery.toLowerCase()) ||
    curr.code.toLowerCase().includes(currencySearchQuery.toLowerCase()) ||
    curr.symbol.includes(currencySearchQuery)
  );

  const handleExport = async (config: ExportConfig) => {
    try {
      console.log('Starting export with config:', config);
      
      // Формируем параметры для API
      let startDate: string | undefined;
      let endDate: string | undefined;
      let periodText = '';

      if (config.periodType === 'custom' && config.startDate && config.endDate) {
        startDate = config.startDate;
        endDate = config.endDate;
        periodText = `${new Date(startDate).toLocaleDateString('ru-RU')} - ${new Date(endDate).toLocaleDateString('ru-RU')}`;
      } else if (config.periodType === 'month' && config.selectedMonth && config.selectedYear) {
        const monthDate = new Date(config.selectedYear, config.selectedMonth - 1, 1);
        startDate = new Date(config.selectedYear, config.selectedMonth - 1, 1).toISOString().split('T')[0];
        endDate = new Date(config.selectedYear, config.selectedMonth, 0).toISOString().split('T')[0];
        periodText = monthDate.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
      } else if (config.periodType === 'quarter' && config.selectedQuarter && config.selectedYear) {
        const startMonth = (config.selectedQuarter - 1) * 3;
        startDate = new Date(config.selectedYear, startMonth, 1).toISOString().split('T')[0];
        endDate = new Date(config.selectedYear, startMonth + 3, 0).toISOString().split('T')[0];
        periodText = `Q${config.selectedQuarter} ${config.selectedYear}`;
      } else if (config.periodType === 'year' && config.selectedYear) {
        startDate = new Date(config.selectedYear, 0, 1).toISOString().split('T')[0];
        endDate = new Date(config.selectedYear, 11, 31).toISOString().split('T')[0];
        periodText = `${config.selectedYear} год`;
      } else if (config.periodType === 'all') {
        periodText = 'За всё время';
      }

      // Получаем данные транзакций
      const transactionsData = await apiClient.getTransactions(
        config.periodType === 'all' ? 'all' : undefined,
        startDate,
        endDate
      );

      // Получаем цели если нужно
      let goals;
      if (config.includeGoals) {
        const goalsData = await apiClient.getGoals();
        goals = goalsData.goals;
      }

      // Генерируем Excel файл
      const excelBlob = await generateExcelFile(
        {
          transactions: transactionsData.transactions || [],
          goals: goals,
          periodText: periodText,
          currency: settings?.currency || 'RUB',
          userName: userName
        },
        config
      );

      const filename = `ALVEON_Export_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Отправляем файл на сервер
      const formData = new FormData();
      formData.append('excel_file', excelBlob, filename);
      formData.append('filename', filename);
      formData.append('period_text', periodText);
      formData.append('export_config', JSON.stringify(config));
      formData.append('file_size_kb', Math.round(excelBlob.size / 1024).toString());

      await apiClient.uploadExport(formData);

      
      // Тактильная обратная связь об успехе
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }

      // Показываем успешное сообщение с предложением перейти к "Сохраненные таблицы"
      const tg = window.Telegram?.WebApp;
      if (tg && tg.showPopup) {
        tg.showPopup({
          title: t('export.exportCompletedTitle'),
          message: t('export.exportCompletedMessage'),
          buttons: [
            { id: 'view', type: 'default', text: t('export.exportCompletedView') },
            { id: 'close', type: 'cancel', text: t('export.exportCompletedClose') }
          ]
        }, (buttonId: string) => {
          if (buttonId === 'view') {
            setShowExportHistory(true);
          }
        });
      } else if (tg && tg.showAlert) {
        tg.showAlert(t('export.exportCompletedMessage'));
      }
    } catch (error) {
      console.error('❌ Export error:', error);
      
      // Тактильная обратная связь об ошибке
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
      
      // Показываем сообщение об ошибке пользователю
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert(t('export.exportError'));
      }
      
      throw error;
    }
  };

  const handleThemeChange = async (mode: ThemeMode) => {
    try {
      setIsUpdatingSettings(true);
      changeThemeMode(mode);
      const response = await apiClient.updateSettings({ theme: mode });
      setSettings(response.settings);
      console.log('✅ Theme updated to:', mode);
    } catch (error) {
      console.error('Error updating theme:', error);
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const handleDeleteAccount = async (reason?: string) => {
    // Тактильная обратная связь
    try {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('warning');
      }
    } catch (e) {
      // Ignore if not supported
    }
    
    try {
      console.log('🗑️ Deleting account...', reason ? `(reason: ${reason})` : '');
      await apiClient.deleteAccount(reason);
      console.log('✅ Account deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting account:', error);
      
      // Тактильная обратная связь об ошибке
      try {
        if (window.Telegram?.WebApp?.HapticFeedback) {
          window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
        }
      } catch (e) {
        // Ignore if not supported
      }
      
      // Выбрасываем ошибку только если удаление НЕ прошло
      throw error;
    }
    
    // Если мы здесь - значит удаление прошло успешно
    // Всё что ниже - не критично, не выбрасываем ошибки
    
    // Устанавливаем флаг что аккаунт был удален
    // Это предотвратит автоматическое создание при следующем входе
    localStorage.setItem('account_deleted', 'true');
    
    // Очищаем токен и все данные
    localStorage.removeItem('auth_token');
    
    // Тактильная обратная связь об успехе
    try {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
    } catch (e) {
      // Ignore if not supported
    }
    
    // Показываем сообщение об успехе (только если API поддерживается)
    try {
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert(t('settings.deleteAccountSuccess'));
      }
    } catch (e) {
      console.log('⚠️ showAlert not supported, skipping');
    }
    
    // Перезагружаем страницу для показа LoginScreen
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  // Порядок секций как в mobile
  const isPremium = ['premium', 'lifetime_premium', 'active', 'trial'].includes(settings?.subscription_status || '');

  const menuSections = [
    { id: 'data', title: t('settings.sections.data'), items: [
      { icon: 'download', label: t('settings.exportData'), value: '', action: 'export' },
      { icon: 'history', label: t('settings.savedTables'), value: '', action: 'export_history' },
    ]},
    { id: 'appearance', title: t('settings.sections.appearance'), items: [
      { icon: 'moon', label: t('settings.theme'), value: '', action: 'theme', isThemeSelector: true },
    ]},
    { id: 'main', title: t('settings.sections.main'), items: [
      { icon: 'globe', label: t('settings.language'), value: getCurrentLanguageName(), action: 'language' },
      { icon: 'wallet', label: t('settings.currency'), value: getCurrencySymbol(settings?.currency), action: 'currency' },
      { icon: 'clock', label: t('settings.timezone'), value: settings?.timezone_city || t('settings.auto'), action: 'timezone' },
    ]},
    { id: 'security', title: t('settings.sections.security'), items: [
      { icon: 'shield', label: t('settings.securityAndLogin'), value: '', action: 'security_screen' },
      { icon: 'smartphone', label: t('settings.devices'), value: '', action: 'devices' },
    ]},
    { id: 'about', title: t('settings.sections.about'), items: [
      { icon: 'info', label: t('settings.version'), value: '1.0.0', action: 'version', isStatic: true },
      { icon: 'help-circle', label: t('settings.helpAndFeedback'), value: '', action: 'feedback' },
    ]},
  ];

  return (
    <div className="settings-screen page">
      <Header title={t('settings.title')} />
      
      {/* Subscription Block — как в mobile (первая секция) */}
      <div className="section">
        <h3 className="section__title">{t('settings.sections.subscription')}</h3>
        <div onClick={() => setCurrentPage('subscription')} style={{ cursor: 'pointer' }}>
          <Card padding="medium" className="settings-card settings-card--glass">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div 
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: settings?.subscription_status === 'premium' || settings?.subscription_status === 'lifetime_premium' 
                    ? 'linear-gradient(135deg, #1C78FF 0%, #4A9FFF 100%)' 
                    : '#F2F2F7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}
              >
                <Icon name={settings?.subscription_status === 'premium' || settings?.subscription_status === 'lifetime_premium' ? 'trophy' : 'user'} size={20} color={settings?.subscription_status === 'premium' || settings?.subscription_status === 'lifetime_premium' ? 'white' : '#8E8E93'} />
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>
                  {settings?.subscription_status === 'lifetime_premium' ? 'Пожизненный Premium' :
                   settings?.subscription_status === 'premium' ? 'Premium' : 'Free'}
                </div>
                <div style={{ fontSize: '13px', color: '#8E8E93' }}>
                  {settings?.subscription_status === 'premium' || settings?.subscription_status === 'lifetime_premium' 
                    ? 'Нажмите для управления' 
                    : 'Откройте все возможности'}
                </div>
              </div>
            </div>
            <Icon name="chevron-right" size={20} color="#8E8E93" />
          </div>
          </Card>
        </div>
      </div>

      {/* Settings Sections */}
      {menuSections.map((section, index) => (
        <div key={index} className="section">
          <h3 className="section__title">{section.title}</h3>
          <div className="section__items">
            {section.items.map((item, itemIndex) => {
              const isStatic = (item as any).isStatic;
              const isThemeSelector = (item as any).isThemeSelector;
              const className = 'settings-item settings-item--last';
              
              if (isStatic) {
                return (
                  <Card key={itemIndex} padding="none" className="settings-item-card settings-card--glass">
                    <div
                      className={className}
                      style={{ cursor: 'default' }}
                    >
                      <div className="settings-item__left">
                        <span className="settings-item__icon">
                          <Icon name={item.icon} size={20} />
                        </span>
                        <span className="settings-item__label">{item.label}</span>
                      </div>
                      <div className="settings-item__right">
                        {item.value && (
                          <span className="settings-item__value">{item.value}</span>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              }

              if (isThemeSelector) {
                const getThemeHint = () => {
                  switch (themeMode) {
                    case 'system':
                      return t('settings.themeSystem') || 'Системная';
                    case 'light':
                      return t('settings.themeLight') || 'Светлая';
                    case 'dark':
                      return t('settings.themeDark') || 'Темная';
                    default:
                      return '';
                  }
                };

                const getThemeIcon = () => {
                  switch (themeMode) {
                    case 'system':
                      return 'monitor';
                    case 'light':
                      return 'sun';
                    case 'dark':
                      return 'moon';
                    default:
                      return 'monitor';
                  }
                };

                const cycleTheme = () => {
                  if (isUpdatingSettings) return;
                  
                  const modes: ThemeMode[] = ['system', 'light', 'dark'];
                  const currentIndex = modes.indexOf(themeMode);
                  const nextIndex = (currentIndex + 1) % modes.length;
                  handleThemeChange(modes[nextIndex]);
                };

                return (
                  <Card key={itemIndex} padding="none" className="settings-item-card settings-card--glass">
                    <div
                      className={`${className} settings-item--theme`}
                    >
                      <div className="settings-item__left">
                        <span className="settings-item__icon">
                          <Icon name={item.icon} size={20} />
                        </span>
                        <span className="settings-item__label">{item.label}</span>
                      </div>
                      <div className="theme-control-wrapper">
                        <button
                          className={`theme-toggle theme-toggle--${themeMode}`}
                          onClick={cycleTheme}
                          disabled={isUpdatingSettings}
                        >
                          <Icon name={getThemeIcon()} size={16} />
                        </button>
                        <div className={`theme-hint theme-hint--${themeMode}`}>{getThemeHint()}</div>
                      </div>
                    </div>
                  </Card>
                );
              }
              
              return (
                <Card key={itemIndex} padding="none" className="settings-item-card settings-card--glass">
                  <button
                    className={className}
                  onClick={() => {
                    if ((item as any).action === 'language') {
                      setShowLanguageModal(true);
                    } else if ((item as any).action === 'currency') {
                      setShowCurrencyModal(true);
                    } else if ((item as any).action === 'timezone') {
                      setShowTimezoneModal(true);
                    } else if ((item as any).action === 'export') {
                      setShowExportModal(true);
                    } else if ((item as any).action === 'export_history') {
                      setShowExportHistory(true);
                    } else if ((item as any).action === 'devices') {
                      setShowDeviceManagement(true);
                    } else if ((item as any).action === 'feedback' || (item as any).action === 'help') {
                      setShowFeedbackModal(true);
                    } else if ((item as any).action === 'set_password') {
                      setPasswordError('');
                      setPasswordInput('');
                      setConfirmPasswordInput('');
                      setShowPasswordModal(true);
                    } else if ((item as any).action === 'security_screen') {
                      setCurrentPage('security');
                    } else {
                      setModalContent({
                        title: item.label,
                        message: t('settings.featureComingSoon', { feature: item.label })
                      });
                      setShowInfoModal(true);
                    }
                  }}
                >
                  <div className="settings-item__left">
                    <span className="settings-item__icon">
                      <Icon name={item.icon} size={20} />
                    </span>
                    <span className="settings-item__label">{item.label}</span>
                  </div>
                  <div className="settings-item__right">
                    {item.value && (
                      <span className="settings-item__value">{item.value}</span>
                    )}
                    <Icon name="chevron-right" size={16} color="#C7C7CC" />
                  </div>
                </button>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {/* Premium Banner - для непремиум */}
      {!isPremium && (
        <div className="section">
          <div 
            className="settings-premium-banner"
            onClick={() => setCurrentPage('subscription')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setCurrentPage('subscription')}
          >
            <div className="settings-premium-banner__content">
              <div className="settings-premium-banner__icon">
                <Icon name="crown" size={28} color="#FFF" />
              </div>
              <div className="settings-premium-banner__text">
                <span className="settings-premium-banner__title">{t('home.premiumBanner.title', 'Откройте Premium')}</span>
                <span className="settings-premium-banner__subtitle">{t('home.premiumBanner.subtitle', 'Все возможности приложения')}</span>
              </div>
            </div>
            <Icon name="chevron-right" size={24} color="#FFF" />
          </div>
        </div>
      )}

      {/* Danger Zone - как в mobile */}
      <div className="section">
        <div className="section__danger-header">
          <Icon name="alert-circle" size={18} />
          <h3 className="section__title section__title--danger">{t('settings.sections.dangerZone')}</h3>
        </div>
        <div className="settings-danger-zone settings-card--glass">
          <button
            className="settings-danger-item"
            onClick={() => setShowDeleteAccountModal(true)}
          >
            <div className="settings-danger-item__left">
              <span className="settings-danger-item__icon">
                <Icon name="account-remove" size={20} />
              </span>
              <div>
                <span className="settings-danger-item__title">{t('settings.deleteAccount')}</span>
                <span className="settings-danger-item__subtitle">{t('settings.deleteAccountDescription', 'Безвозвратно удалить аккаунт и все данные')}</span>
              </div>
            </div>
            <Icon name="chevron-right" size={20} />
          </button>
          <button
            className="settings-danger-item"
            onClick={() => {
              if (confirm(t('settings.deleteConfirm'))) {
                apiClient.deleteAllData();
              }
            }}
          >
            <div className="settings-danger-item__left">
              <span className="settings-danger-item__icon">
                <Icon name="delete-sweep" size={20} />
              </span>
              <div>
                <span className="settings-danger-item__title">{t('settings.deleteAllData')}</span>
                <span className="settings-danger-item__subtitle">{t('settings.deleteDataDescription', 'Удалить все транзакции и данные')}</span>
              </div>
            </div>
            <Icon name="chevron-right" size={20} />
          </button>
          <div className="settings-danger-divider" />
          <button
            className="settings-danger-logout"
            onClick={async () => {
              if (confirm(t('settings.logoutConfirm'))) {
                localStorage.removeItem('auth_token');
                await apiClient.setToken('');
                window.location.reload();
              }
            }}
          >
            <Icon name="logout" size={20} color="#FFFFFF" />
            <span>{t('settings.logoutFromAccount', t('settings.logout'))}</span>
          </button>
        </div>
      </div>

      {/* Currency Modal */}
      <Modal
        isOpen={showCurrencyModal}
        onClose={() => {
          if (!isUpdatingSettings) {
            setShowCurrencyModal(false);
            setCurrencySearchQuery('');
          }
        }}
        title={t('settings.selectCurrency')}
        size="55vh"
        position="bottom"
      >
            
            {/* Поле поиска */}
            <div style={{ 
              position: 'relative', 
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Icon 
                  name="search" 
                  size={18} 
                  color="var(--color-text-secondary)" 
                  style={{ 
                    position: 'absolute', 
                    left: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    zIndex: 1
                  }} 
                />
                <input
                  type="text"
                  placeholder={t('settings.searchCurrency') || 'Поиск валюты...'}
                  value={currencySearchQuery}
                  onChange={(e) => setCurrencySearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    borderRadius: '12px',
                    border: 'none',
                    fontSize: '15px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    background: 'var(--neo-bg)',
                    color: 'var(--color-text-primary)',
                    boxShadow: 'inset 4px 4px 10px var(--neo-shadow-dark), inset -4px -4px 10px var(--neo-shadow-light)',
                    transition: 'box-shadow 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.boxShadow = 'inset 5px 5px 12px var(--neo-shadow-dark), inset -5px -5px 12px var(--neo-shadow-light)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.boxShadow = 'inset 4px 4px 10px var(--neo-shadow-dark), inset -4px -4px 10px var(--neo-shadow-light)';
                  }}
                />
              </div>
              {currencySearchQuery && (
                <button
                  onClick={() => setCurrencySearchQuery('')}
                  style={{
                    padding: '8px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-secondary)'
                  }}
                >
                  <Icon name="close" size={20} />
                </button>
              )}
            </div>

            {/* Список валют */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '8px',
              overflowY: 'auto',
              flex: 1,
              marginBottom: '16px',
              padding: '20px 20px 0 20px'
            }}>
              {filteredCurrencies.length > 0 ? (
                filteredCurrencies.map((curr) => (
                  <button
                    key={curr.code}
                    onClick={() => {
                      handleCurrencyChange(curr.code);
                      setCurrencySearchQuery('');
                    }}
                    disabled={isUpdatingSettings}
                    style={{
                      padding: '16px',
                      borderRadius: '12px',
                      border: settings?.currency === curr.code ? '2px solid var(--color-primary)' : 'none',
                      background: 'var(--neo-bg)',
                      boxShadow: settings?.currency === curr.code ? 'inset 4px 4px 10px var(--neo-shadow-dark), inset -4px -4px 10px var(--neo-shadow-light)' : '3px 3px 6px var(--neo-shadow-dark), -3px -3px 6px var(--neo-shadow-light)',
                      cursor: isUpdatingSettings ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      textAlign: 'left',
                      transition: 'all 0.2s',
                      color: 'var(--color-text-primary)'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                        {curr.name}
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                        {curr.code}
                      </div>
                    </div>
                    <div style={{ fontSize: '24px' }}>
                      {curr.symbol}
                    </div>
                  </button>
                ))
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 20px', 
                  color: 'var(--color-text-secondary)',
                  fontSize: '15px'
                }}>
                  {t('settings.noCurrenciesFound') || 'Валюты не найдены'}
                </div>
              )}
            </div>
            
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowCurrencyModal(false);
                setCurrencySearchQuery('');
              }}
              style={{ width: '100%' }}
              className="neo-raised"
              disabled={isUpdatingSettings}
            >
              {t('common.cancel')}
            </Button>
      </Modal>

      {/* Info Modal */}
      {showInfoModal && (
        <div 
          className="modal-overlay modal-overlay--center"
          onClick={() => setShowInfoModal(false)}
        >
          <div
            className="modal-content"
            style={{ 
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '400px', 
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '20px', textAlign: 'center' }}>
              {modalContent.title}
            </h3>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ marginBottom: '16px' }}>
                <Icon name="settings" size={48} color="#1C78FF" />
              </div>
              <p style={{ color: '#86868B', marginBottom: '24px', fontSize: '16px' }}>
                {modalContent.message}
              </p>
              <Button 
                variant="primary" 
                onClick={() => setShowInfoModal(false)}
                style={{ width: '100%' }}
              >
                {t('common.close')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Language Modal */}
      <Modal
        isOpen={showLanguageModal}
        onClose={() => !isUpdatingSettings && setShowLanguageModal(false)}
        title={t('settings.selectLanguage')}
        size="55vh"
        position="bottom"
      >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '20px 20px 0 20px' }}>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  disabled={isUpdatingSettings}
                  style={{
                    padding: '16px',
                    borderRadius: '12px',
                    border: (settings?.language || i18n.language) === lang.code ? '2px solid var(--color-primary)' : 'none',
                    background: 'var(--neo-bg)',
                    boxShadow: (settings?.language || i18n.language) === lang.code ? 'inset 4px 4px 10px var(--neo-shadow-dark), inset -4px -4px 10px var(--neo-shadow-light)' : '3px 3px 6px var(--neo-shadow-dark), -3px -3px 6px var(--neo-shadow-light)',
                    cursor: isUpdatingSettings ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    textAlign: 'left',
                    transition: 'all 0.2s',
                    color: 'var(--color-text-primary)'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '600' }}>
                      {lang.name}
                    </div>
                  </div>
                  {(settings?.language || i18n.language) === lang.code && (
                    <Icon name="check" size={20} color="var(--color-primary)" />
                  )}
                </button>
              ))}
            </div>
            <Button 
              variant="secondary" 
              onClick={() => setShowLanguageModal(false)}
              style={{ width: '100%', marginTop: '16px' }}
              className="neo-raised"
              disabled={isUpdatingSettings}
            >
              {t('common.cancel')}
            </Button>
      </Modal>

      {/* Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => {
          if (!isSettingPassword) {
            setShowPasswordModal(false);
            setPasswordError('');
            setPasswordInput('');
            setConfirmPasswordInput('');
          }
        }}
        title={hasPassword ? 'Изменить пароль' : 'Установить пароль'}
        size="auto"
        position="bottom"
      >
        <div style={{ padding: '4px 0' }}>
          <p style={{ color: '#8E8E93', fontSize: '14px', marginBottom: '20px', lineHeight: '1.4' }}>
            {hasPassword 
              ? 'Введите новый пароль для входа в приложение вне Telegram'
              : `Установите пароль для входа в приложение вне Telegram. Ваш Telegram ID: ${userId}`
            }
          </p>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: 'var(--color-text-primary)' }}>
              {hasPassword ? 'Новый пароль' : 'Пароль'}
            </label>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Минимум 8 символов"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '12px',
                border: passwordError ? '2px solid #FF3B30' : 'none',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box',
                background: 'var(--neo-bg)',
                color: 'var(--color-text-primary)',
                boxShadow: 'inset 4px 4px 10px var(--neo-shadow-dark), inset -4px -4px 10px var(--neo-shadow-light)',
              }}
              disabled={isSettingPassword}
            />
          </div>
          
          <div style={{ marginBottom: passwordError ? '8px' : '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: 'var(--color-text-primary)' }}>
              Подтвердите пароль
            </label>
            <input
              type="password"
              value={confirmPasswordInput}
              onChange={(e) => setConfirmPasswordInput(e.target.value)}
              placeholder="Повторите пароль"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '12px',
                border: passwordError ? '2px solid #FF3B30' : 'none',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box',
                background: 'var(--neo-bg)',
                color: 'var(--color-text-primary)',
                boxShadow: 'inset 4px 4px 10px var(--neo-shadow-dark), inset -4px -4px 10px var(--neo-shadow-light)',
              }}
              disabled={isSettingPassword}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isSettingPassword) {
                  handleSetPassword();
                }
              }}
            />
          </div>
          
          {passwordError && (
            <p style={{ color: '#FF3B30', fontSize: '13px', marginBottom: '16px' }}>
              {passwordError}
            </p>
          )}
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowPasswordModal(false);
                setPasswordError('');
                setPasswordInput('');
                setConfirmPasswordInput('');
              }}
              disabled={isSettingPassword}
              style={{ flex: 1 }}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleSetPassword}
              disabled={isSettingPassword || !passwordInput || !confirmPasswordInput}
              style={{ flex: 1 }}
            >
              {isSettingPassword ? 'Сохранение...' : (hasPassword ? 'Изменить' : 'Установить')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div 
          className="modal-overlay modal-overlay--bottom"
          onClick={() => setShowFeedbackModal(false)}
        >
          <div
            className="modal-content modal-content--bottom-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '12px', textAlign: 'center', fontSize: '20px', fontWeight: '700' }}>
              {t('settings.feedback')}
            </h3>
            <p style={{ textAlign: 'center', color: '#86868B', marginBottom: '24px', fontSize: '15px' }}>
              {t('settings.feedbackDescription')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                className="neo-raised"
                onClick={() => {
                  window.open('mailto:help@alveon.app', '_blank');
                  setShowFeedbackModal(false);
                }}
                style={{
                  padding: '16px',
                  borderRadius: '12px',
                  backgroundColor: '#007AFF',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none'
                }}
              >
                <Icon name="mail" size={20} color="white" />
                help@alveon.app
              </button>
              <button
                className="neo-raised"
                onClick={() => {
                  window.open('https://t.me/andrei_florida', '_blank');
                  setShowFeedbackModal(false);
                }}
                style={{
                  padding: '16px',
                  borderRadius: '12px',
                  backgroundColor: '#0088CC',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: 'none'
                }}
              >
                <Icon name="send" size={20} color="white" />
                @andrei_florida
              </button>
            </div>
            <Button 
              variant="secondary" 
              onClick={() => setShowFeedbackModal(false)}
              style={{ width: '100%', marginTop: '16px' }}
              className="neo-raised"
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* Timezone Modal */}
      {showTimezoneModal && (
        <TimezoneSelector
          currentTimezone={settings?.timezone}
          onSelect={handleTimezoneChange}
          onClose={() => setShowTimezoneModal(false)}
          isUpdating={isUpdatingSettings}
        />
      )}


      {/* Export Modal */}
      {showExportModal && (
        <ExportFlow
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          currency={settings?.currency || 'RUB'}
        />
      )}

      {/* Export History Modal */}
      {showExportHistory && (
        <ExportHistoryScreen onClose={() => setShowExportHistory(false)} />
      )}

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        onConfirm={handleDeleteAccount}
      />

      {/* Device Management Screen */}
      {showDeviceManagement && (
        <DeviceManagementScreen onClose={() => setShowDeviceManagement(false)} />
      )}
    </div>
  );
};
